/**
 * Edits that fix check findings while keeping what refers to the circuits: letters, device
 * tags and the codes of points on the position plan.
 */
import {
  applyTransaction,
  newId,
  nextCircuitCode,
  type Element,
  type Id,
  type Op,
  type Project,
  type PropValue,
} from '@elec/domain';
import { analyse, controlInfo, RCD_DEVICES } from '@elec/analysis';
import { paperSize } from '@elec/layout';
import { elementBounds, getSymbol } from '@elec/symbols';
import {
  appendCircuitOps,
  appendGroupOps,
  appendPointsOps,
  deleteCircuitOps,
  setPointsOps,
  specOf,
  type CircuitSpec,
} from './singleLine.ts';

const apply = (p: Project, ops: Op[]) => applyTransaction(p, { label: '', ops }).project;
const onPlan = (p: Project, e: Element | undefined) => !!e && p.sheets[e.sheetId]?.kind === 'position-plan';
const POINT_TAG = /^([A-Z]+)(\d+)$/;

/** Ops giving the circuits added by `added` (temporary letters) their final letter and device tag. */
function retagOps(after: Project, added: Op[], final: Map<Id, { code: string; deviceTag?: string }>): Op[] {
  const ops: Op[] = [];
  const codes = new Map<string, string>();
  for (const o of added)
    if (o.op === 'add' && o.coll === 'circuits' && final.has(o.value.id)) {
      const f = final.get(o.value.id)!;
      if (f.code !== o.value.code) {
        codes.set(o.value.code, f.code);
        ops.push({ op: 'update', coll: 'circuits', id: o.value.id, patch: { code: f.code } });
      }
    }
  for (const o of added) {
    if (o.op !== 'add' || o.coll !== 'elements' || !after.elements[o.value.id]) continue;
    const e = o.value;
    const f = e.circuitId ? final.get(e.circuitId) : undefined;
    if (f?.deviceTag && e.tag !== f.deviceTag) {
      ops.push({ op: 'update', coll: 'elements', id: e.id, patch: { tag: f.deviceTag } });
      continue;
    }
    const m = POINT_TAG.exec(e.tag);
    if (m && codes.has(m[1]!))
      ops.push({ op: 'update', coll: 'elements', id: e.id, patch: { tag: codes.get(m[1]!)! + m[2] } });
  }
  return ops;
}

/**
 * A new RCD at the right of the board with new circuits and/or existing ones moved under it
 * (R-RCD-04: split a 30 mA group; R-RCD-05: an EV charger alone behind its own RCD). Moved
 * circuits keep their letter, device tag, points and cable, and their points on the plan.
 */
export function regroupOps(
  project: Project,
  sheetId: Id,
  rcd: Record<string, PropValue>,
  circuits: CircuitSpec[],
  moveDeviceIds: Id[],
): { ops: Op[]; rcdId?: Id } {
  const an = analyse(project);
  const moved = moveDeviceIds.flatMap((id) => {
    const c = an.circuits.find((x) => x.device.id === id);
    const spec = specOf(project, id, an);
    return c && spec ? [{ id, spec, code: c.circuit?.code ?? c.code, tag: c.device.tag }] : [];
  });
  // Drawn first, while the old circuits still hold their letters and tags (no clash), then the
  // old columns are removed and the moved ones take their letters back.
  const group = appendGroupOps(project, sheetId, rcd, [...moved.map((m) => m.spec), ...circuits], an);
  let p = apply(project, group);
  const ops = [...group];
  for (const m of moved) {
    const del = deleteCircuitOps(p, m.id, analyse(p), true);
    ops.push(...del);
    p = apply(p, del);
  }
  const added = group.flatMap((o) => (o.op === 'add' && o.coll === 'circuits' ? [o.value] : []));
  const final = new Map(
    added.slice(0, moved.length).map((c, i) => [c.id, { code: moved[i]!.code, deviceTag: moved[i]!.tag }]),
  );
  ops.push(...retagOps(p, group, final));
  const rcdOp = group.find(
    (o) => o.op === 'add' && o.coll === 'elements' && RCD_DEVICES.has(o.value.symbolId),
  );
  return { ops, ...(rcdOp?.op === 'add' && rcdOp.coll === 'elements' ? { rcdId: rcdOp.value.id } : {}) };
}

/**
 * Move some points of a circuit to a new circuit beside it, under the same RCD (R-CIR-01: more
 * than 8 points; R-CIR-03: an appliance alone on its circuit). Both circuits are numbered again
 * (A1…, B1…) and the points on the position plan take the new codes, keeping their positions.
 */
export function splitCircuitOps(
  project: Project,
  deviceId: Id,
  moveIds: Id[],
  newCircuit: { label: string; deviceProps?: Record<string, PropValue>; rcdId?: Id },
): { ops: Op[]; code?: string } {
  const an = analyse(project);
  const c = an.circuits.find((x) => x.device.id === deviceId);
  const spec = specOf(project, deviceId, an);
  if (!c || !spec) return { ops: [] };
  const own = [...c.points]
    .filter((x) => x.element.sheetId === c.device.sheetId)
    .sort((a, b) => a.element.y - b.element.y);
  const move = new Set(moveIds);
  const moved = own.filter((x) => move.has(x.element.id));
  const kept = own.filter((x) => !move.has(x.element.id));
  if (!moved.length || !kept.length) return { ops: [] };
  const code = c.circuit?.code ?? c.code;
  const newCode = nextCircuitCode(project);
  const ops1 = appendCircuitOps(
    project,
    c.device.sheetId,
    {
      ...spec,
      label: newCircuit.label,
      device: { ...spec.device, props: { ...spec.device.props, ...newCircuit.deviceProps } },
      points: moved.map((x) => ({ symbolId: x.element.symbolId, props: { ...x.element.props } })),
    },
    newCircuit.rcdId ?? c.rcds[0]?.id,
    an,
  );
  const p1 = apply(project, ops1);
  // The old circuit keeps the rest; the plan's points are retagged below, not removed.
  const ops2 = setPointsOps(
    p1,
    deviceId,
    kept.map((x) => ({ symbolId: x.element.symbolId, props: { ...x.element.props } })),
    analyse(p1),
    kept.map((x) => x.element.id),
  ).filter((o) => {
    if (o.op !== 'remove') return true;
    if (o.coll === 'elements') return !onPlan(p1, p1.elements[o.id]);
    if (o.coll === 'wires') return p1.sheets[p1.wires[o.id]?.sheetId ?? '']?.kind !== 'position-plan';
    return true;
  });
  const retag = new Map<string, string>();
  moved.forEach((x, i) => retag.set(x.element.tag, `${newCode}${i + 1}`));
  kept.forEach((x, i) => retag.set(x.element.tag, `${code}${i + 1}`));
  const plan: Op[] = Object.values(project.elements)
    .filter((e) => onPlan(project, e) && retag.has(e.tag) && retag.get(e.tag) !== e.tag)
    .map((e) => ({ op: 'update', coll: 'elements', id: e.id, patch: { tag: retag.get(e.tag)! } }));
  return { ops: [...ops1, ...ops2, ...plan], code: newCode };
}

/** Sockets without an earth contact replaced by earthed ones, on the diagram and the plan (R-CIR-05). */
export function earthSocketsOps(project: Project, codes?: string[]): Op[] {
  const want = codes && new Set(codes.map((x) => x.toUpperCase()));
  return Object.values(project.elements)
    .filter((e) => e.symbolId === 'iec.socket-noearth')
    .filter((e) => !want || want.has(POINT_TAG.exec(e.tag)?.[1] ?? ''))
    .map((e) => ({
      op: 'update',
      coll: 'elements',
      id: e.id,
      patch: { symbolId: 'iec.socket', props: { ...e.props } },
    }));
}

/** A protective device's or RCD's tag on the single-line diagrams. */
export const deviceByTag = (project: Project, tag: string) =>
  Object.values(project.elements).find(
    (e) =>
      e.tag.toUpperCase() === tag.toUpperCase() &&
      getSymbol(e.symbolId)?.category === 'protection' &&
      project.sheets[e.sheetId]?.kind === 'single-line',
  );

// ---------- one-click fixes of the check panel ----------

const centre = (e: Element) => {
  const d = getSymbol(e.symbolId);
  const b = d ? elementBounds(d, e) : { minX: e.x, maxX: e.x, minY: e.y, maxY: e.y };
  return { x: (b.minX + b.maxX) / 2, y: (b.minY + b.maxY) / 2 };
};

/**
 * Cable lengths estimated from a position plan drawn on an imported, scaled floor plan: from the
 * board to the circuit's points one after the other (nearest first), along the walls (|dx| + |dy|),
 * plus 10 % and 2 m for the drops. Only for circuits with no length; marked as estimated.
 */
export function estimateLengthsOps(project: Project): { ops: Op[]; codes: string[] } {
  const an = analyse(project);
  const plans = Object.values(project.sheets).filter((s) => s.kind === 'position-plan' && s.background);
  const ops: Op[] = [];
  const codes: string[] = [];
  for (const c of an.circuits) {
    if (!c.circuit || c.circuit.cable.lengthM || c.circuit.spare) continue;
    const tags = new Set(c.points.map((x) => x.element.tag).filter(Boolean));
    for (const s of plans) {
      const on = Object.values(project.elements).filter((e) => e.sheetId === s.id);
      const board = on.find((e) => e.symbolId === 'iec.board');
      const pts = on.filter((e) => tags.has(e.tag) && e.symbolId !== 'iec.board');
      if (!board || !pts.length) continue;
      let at = centre(board);
      let mm = 0;
      const left = pts.map(centre);
      while (left.length) {
        left.sort(
          (a, b) =>
            Math.abs(a.x - at.x) + Math.abs(a.y - at.y) - (Math.abs(b.x - at.x) + Math.abs(b.y - at.y)),
        );
        const next = left.shift()!;
        mm += Math.abs(next.x - at.x) + Math.abs(next.y - at.y);
        at = next;
      }
      const m = Math.ceil(((mm * (s.planScale ?? 50)) / 1000) * 1.1 + 2);
      ops.push({
        op: 'update',
        coll: 'circuits',
        id: c.circuit.id,
        patch: { cable: { ...c.circuit.cable, lengthM: m, lengthEstimated: true } },
      });
      codes.push(c.code);
      break;
    }
  }
  return { ops, codes };
}

/** Can the elements of an A4 sheet go on A3 without moving (R-DOC-04)? */
export function fitsOnA3(project: Project, sheetId: Id): boolean {
  const s = project.sheets[sheetId];
  if (!s || s.paper !== 'A4') return false;
  const { w, h } = paperSize({ ...s, paper: 'A3' });
  return Object.values(project.elements)
    .filter((e) => e.sheetId === sheetId)
    .every((e) => {
      const d = getSymbol(e.symbolId);
      if (!d) return true;
      const b = elementBounds(d, e);
      return b.minX >= 0 && b.minY >= 0 && b.maxX <= w && b.maxY <= h;
    });
}

const fold = (t: string) => t.toUpperCase().replace(/[\s\-_.'’]/g, '');
/** Levenshtein distance ≤ 1. */
function near(a: string, b: string): boolean {
  if (a === b) return true;
  if (Math.abs(a.length - b.length) > 1) return false;
  let i = 0;
  while (i < a.length && i < b.length && a[i] === b[i]) i++;
  return a.slice(i + 1) === b.slice(i + 1) || a.slice(i) === b.slice(i + 1) || a.slice(i + 1) === b.slice(i);
}

/**
 * The tag a contact without coil (or a coil without contacts) was probably meant to have: the
 * one relay on the other side whose tag differs by a typo (case, a space, one character).
 */
export function likelyTag(project: Project, tag: string, side: 'coil' | 'contact'): string | null {
  const relays = [...controlInfo(project).relays.values()];
  const others = relays.filter(
    (r) =>
      r.tag !== tag && (side === 'coil' ? r.heads.length > 0 : r.contacts.length > 0 && r.heads.length === 0),
  );
  const hits = others.filter((r) => near(fold(r.tag), fold(tag)));
  return hits.length === 1 ? hits[0]!.tag : null;
}

/** Rename the elements tagged `from` (all sheets) to `to`. */
export const renameTagOps = (project: Project, ids: Id[], to: string): Op[] =>
  ids
    .filter((id) => project.elements[id])
    .map((id) => ({ op: 'update', coll: 'elements', id, patch: { tag: to } }));

/** An element drawn by mistake: no tag, in no circuit (R-TOPO-01 "Delete"). */
export function isStray(project: Project, id: Id): boolean {
  const e = project.elements[id];
  return !!e && !e.tag && !e.circuitId && getSymbol(e.symbolId)?.category !== 'protection';
}

/**
 * Connect a point that has no protection to a circuit (R-TOPO-02): it is drawn again at the end
 * of that circuit, with the circuit's next code; the stray copy goes.
 */
export function attachPointOps(project: Project, pointId: Id, code: string): Op[] {
  const e = project.elements[pointId];
  const an = analyse(project);
  const c = an.circuits.find((x) => x.code === code);
  if (!e || !c) return [];
  const remove: Op[] = [
    ...Object.values(project.wires)
      .filter((w) => [w.from, w.to].some((x) => x.kind === 'port' && x.elementId === pointId))
      .map((w): Op => ({ op: 'remove', coll: 'wires', id: w.id })),
    { op: 'remove', coll: 'elements', id: pointId },
  ];
  const p1 = apply(project, remove);
  return [...remove, ...appendPointsOps(p1, c.device.id, [{ symbolId: e.symbolId, props: { ...e.props } }])];
}

/**
 * A room around points that are in none (R-PLAN-01): the box around them and their unroomed
 * neighbours (within 40 mm), 8 mm of margin. Its type is to be chosen ("other" until then).
 */
export function roomAroundOps(project: Project, pointId: Id): { ops: Op[]; id?: Id } {
  const e = project.elements[pointId];
  if (!e) return { ops: [] };
  const rooms = Object.values(project.elements).filter(
    (x) => x.sheetId === e.sheetId && x.symbolId === 'plan.room',
  );
  const inRoom = (x: Element) => {
    const c = centre(x);
    return rooms.some((r) => {
      const b = elementBounds(getSymbol('plan.room')!, r);
      return c.x >= b.minX && c.x <= b.maxX && c.y >= b.minY && c.y <= b.maxY;
    });
  };
  const c0 = centre(e);
  const group = Object.values(project.elements).filter(
    (x) =>
      x.sheetId === e.sheetId &&
      x.tag &&
      x.symbolId !== 'plan.room' &&
      !inRoom(x) &&
      Math.hypot(centre(x).x - c0.x, centre(x).y - c0.y) <= 40,
  );
  const boxes = group.map((x) => elementBounds(getSymbol(x.symbolId)!, x));
  const minX = Math.min(...boxes.map((b) => b.minX)) - 8;
  const minY = Math.min(...boxes.map((b) => b.minY)) - 12;
  const maxX = Math.max(...boxes.map((b) => b.maxX)) + 8;
  const maxY = Math.max(...boxes.map((b) => b.maxY)) + 8;
  const id = newId();
  return {
    id,
    ops: [
      {
        op: 'add',
        coll: 'elements',
        value: {
          id,
          sheetId: e.sheetId,
          symbolId: 'plan.room',
          x: minX,
          y: minY,
          rotation: 0,
          mirrored: false,
          tag: '',
          props: { name: '', roomType: 'other', w: Math.ceil(maxX - minX), h: Math.ceil(maxY - minY) },
        },
      },
    ],
  };
}
